// 🔧 volunteer_add_form.js
// ✅ Purpose: Form component for assigning a volunteer to a specific volunteer time slot
// ✅ Features:
// - Accepts first name, last name, email, and optional phone
// - Looks up an existing global user by email
// - Creates a pending volunteer user if no matching user exists
// - Adds volunteer snapshot to the selected time slot
// - Keeps assignedUserIds for backward compatibility
// - Calls parent onAdded callback so volunteers.js refreshes immediately
// - Uses Bootstrap styling consistent with LGV admin pages
// - Shows success/error notifications through parent setStatus helper
// - Includes Cancel button for closing the form

import React, { useState } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { showStatus } from "../utils/status_helpers";
import { logger } from "../utils/logger";

const VolunteerAddForm = ({
  activityId,
  slotId,
  eventId,
  setStatus,
  onAdded,
  onCancel,
}) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();

    if (!firstName || !lastName || !email) {
      showStatus(setStatus, {
        type: "warning",
        message: "First name, last name, and email are required.",
      });

      return;
    }

    setSubmitting(true);

    try {
      logger.log("🙋 Assigning volunteer to shift...", {
        value: {
          eventId,
          activityId,
          slotId,
          email,
        },
      });

      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", email),
        limit(1)
      );

      const usersSnap = await getDocs(usersQuery);
      const match = usersSnap.empty ? null : usersSnap.docs[0];

      let userId;

      if (match) {
        userId = match.id;
      } else {
        const newUserRef = doc(collection(db, "users"));
        userId = newUserRef.id;

        await setDoc(newUserRef, {
          firstName,
          lastName,
          email,
          phone,

          role: "pending",
          userType: "pendingVolunteer",
          loginMethod: null,
          emailVerified: false,
          isVolunteer: true,

          invitedAt: new Date().toISOString(),

          eventInvites: {
            [eventId]: {
              role: "volunteer",
              status: "active",
              eventId,
              invitedAt: new Date().toISOString(),
            },
          },
        });
      }

      const slotRef = doc(
        db,
        "events",
        eventId,
        "volunteers",
        activityId,
        "timeSlots",
        slotId
      );

      const slotSnap = await getDoc(slotRef);
      const slotData = slotSnap.data() || {};

      const currentAssignedUsers = Array.isArray(slotData.assignedUsers)
        ? slotData.assignedUsers
        : [];

      const currentAssignedUserIds = Array.isArray(slotData.assignedUserIds)
        ? slotData.assignedUserIds
        : [];

      const alreadyAssigned =
        currentAssignedUsers.some((assignedUser) => assignedUser.uid === userId) ||
        currentAssignedUserIds.includes(userId);

      if (alreadyAssigned) {
        showStatus(setStatus, {
          type: "warning",
          message: "This volunteer is already assigned to this shift.",
        });

        setSubmitting(false);
        return;
      }

      const volunteerPayload = {
        uid: userId,
        firstName,
        lastName,
        email,
        phone,
      };

      await updateDoc(slotRef, {
        assignedUsers: [...currentAssignedUsers, volunteerPayload],
        assignedUserIds: [...currentAssignedUserIds, userId],
      });

      showStatus(setStatus, {
        type: "success",
        message: "✅ Volunteer assigned successfully.",
      });

      if (onAdded) {
        await onAdded(volunteerPayload);
      }

      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      });
    } catch (err) {
      logger.error("❌ Error adding volunteer", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "❌ Failed to assign volunteer.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card border shadow-sm">
      <div className="card-header bg-light">
        <strong>🙋 New Volunteer Info</strong>
        <div className="small text-muted">
          Assign a volunteer to this specific shift.
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label fw-bold">
                First Name*
              </label>

              <input
                type="text"
                name="firstName"
                className="form-control"
                value={form.firstName}
                onChange={handleChange}
                disabled={submitting}
                placeholder="First name"
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-bold">
                Last Name*
              </label>

              <input
                type="text"
                name="lastName"
                className="form-control"
                value={form.lastName}
                onChange={handleChange}
                disabled={submitting}
                placeholder="Last name"
              />
            </div>

            <div className="col-12 col-md-7">
              <label className="form-label fw-bold">
                Email*
              </label>

              <input
                type="email"
                name="email"
                className="form-control"
                value={form.email}
                onChange={handleChange}
                disabled={submitting}
                placeholder="volunteer@example.com"
              />
            </div>

            <div className="col-12 col-md-5">
              <label className="form-label fw-bold">
                Phone
              </label>

              <input
                type="text"
                name="phone"
                className="form-control"
                value={form.phone}
                onChange={handleChange}
                disabled={submitting}
                placeholder="Optional"
              />
            </div>

            <div className="col-12">
              <div className="form-text">
                If this person does not already have an account, LGV will create a pending volunteer profile for them.
              </div>
            </div>

            <div className="col-12 d-flex flex-wrap gap-2">
              <button
                type="submit"
                className="btn btn-success"
                disabled={
                  submitting ||
                  !form.firstName.trim() ||
                  !form.lastName.trim() ||
                  !form.email.trim()
                }
              >
                {submitting ? "Assigning..." : "✅ Assign Volunteer"}
              </button>

              {onCancel && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VolunteerAddForm;